import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { JournalCard } from '../components/JournalCard';
import { VideoCard } from '../components/VideoCard';
import { NeedFromAbby } from '../components/NeedFromAbby';
import { NewsletterBand } from '../components/NewsletterBand';
import { TickDivider } from '../components/TickDivider';
import { usePageMeta } from '../hooks/usePageMeta';
import { articleCategories, articles, transitNotes, videos } from '../data/journal';
import type { ArticleCategory } from '../types/content';
type Tab = 'articles' | 'videos' | 'transits';
const TABS: {
  id: Tab;
  label: string;
}[] = [{
  id: 'articles',
  label: 'Articles'
}, {
  id: 'videos',
  label: 'Videos'
}, {
  id: 'transits',
  label: 'Current Transits'
}];
export function Journal() {
  usePageMeta('The Journal | Timing, Ritual & Vedic Wisdom', 'Articles, videos and current transits from True Twelve: timing, relationships, ritual and the basics of Vedic astrology.');
  const [tab, setTab] = useState<Tab>('articles');
  const [category, setCategory] = useState<ArticleCategory | 'All'>('All');
  const filtered = useMemo(() => category === 'All' ? articles : articles.filter(a => a.category === category), [category]);
  const [featured, ...rest] = filtered;
  function onTabKey(e: React.KeyboardEvent, i: number) {
    if (e.key !== 'ArrowRight' && e.key !== 'ArrowLeft') return;
    const next = (i + (e.key === 'ArrowRight' ? 1 : TABS.length - 1)) % TABS.length;
    setTab(TABS[next].id);
    document.getElementById(`tab-${TABS[next].id}`)?.focus();
  }
  return <>
      <section aria-labelledby="journal-title" className="gutter pb-10 pt-[clamp(3rem,7vw,8rem)]" data-id="element-743" data-mp="25onvffp4l9rj">
        <p className="t-label flex items-center gap-3 text-graphite" data-id="element-744" data-mp="20pkvg9o05tqf">
          <span className="t-numeral text-gold-deep" data-id="element-745" data-mp="1wng096hwop1o">III</span>
          <span aria-hidden="true" className="text-gold" data-id="element-746" data-mp="1wp0tmsshi1xy">
            ·
          </span>
          The Journal
        </p>
        <h1 id="journal-title" className="t-display mt-6 max-w-[16ch]" data-id="element-747" data-mp="1i090zy1s9fim">
          Notes on time, ritual and the sky.
        </h1>
        <div role="tablist" aria-label="Journal sections" className="mt-[clamp(2.5rem,5vw,5rem)] flex gap-[clamp(1.5rem,3vw,3.5rem)] overflow-x-auto border-b border-stone" data-id="element-748" data-mp="1m22thj1trd7p">
          {TABS.map((t, i) => <button key={t.id} id={`tab-${t.id}`} role="tab" type="button" aria-selected={tab === t.id} aria-controls={`panel-${t.id}`} tabIndex={tab === t.id ? 0 : -1} onClick={() => setTab(t.id)} onKeyDown={e => onTabKey(e, i)} className={`t-label relative shrink-0 pb-4 transition-colors duration-150 ${tab === t.id ? 'text-charcoal after:absolute after:inset-x-0 after:-bottom-px after:h-px after:bg-charcoal' : 'text-graphite hover:text-charcoal'}`} data-id="element-749" data-mp="1d27ipfc6lk87" data-mpi={i}>
              {t.label}
            </button>)}
        </div>
      </section>

      <div className="gutter pb-[clamp(4rem,8vw,9rem)]" data-id="element-750" data-mp="czho3ok86f8w">
        {tab === 'articles' && <div id="panel-articles" role="tabpanel" aria-labelledby="tab-articles" data-id="element-751" data-mp="81zgc1hy262h">
            <ul className="flex flex-wrap gap-2" aria-label="Filter by category" data-id="element-752" data-mp="lscpu8ahhstx">
              {(['All', ...articleCategories] as const).map(c => <li key={c} data-id="element-753" data-mp="3q7kszf00wiq4">
                  <button type="button" aria-pressed={category === c} onClick={() => setCategory(c)} className={`t-label h-10 border px-4 text-[0.62rem] transition-colors duration-150 ${category === c ? 'border-charcoal bg-charcoal text-white' : 'border-[#8F877F] text-charcoal hover:border-charcoal'}`} data-id="element-754" data-mp="3q8vxjh1aluia">
                    {c}
                  </button>
                </li>)}
            </ul>

            {featured ? <>
                <article className="group relative mt-12 grid items-center gap-8 lg:grid-cols-[minmax(0,1.4fr)_minmax(0,1fr)] lg:gap-[clamp(2.5rem,5vw,7rem)]" data-id="element-755" data-mp="24ugky7re2mz1">
                  <div className="overflow-hidden bg-stone" data-id="element-756" data-mp="2erky5i39b83p">
                    <img src={featured.image} alt={featured.imageAlt} className="aspect-[3/2] w-full object-cover transition-transform duration-300 ease-out group-hover:scale-[1.02]" data-id="element-757" data-mp="2eq04duornt6w" />
                  </div>
                  <div data-id="element-758" data-mp="2nrzlemzs6x9u">
                    <p className="t-label text-[0.64rem] text-gold-deep" data-id="element-759" data-mp="2srchzzggfipm">Featured · {featured.category}</p>
                    <h2 className="t-h2 mt-4" data-id="element-760" data-mp="2xicdd09xdsq0">
                      <Link to={`/journal/${featured.slug}`} className="after:absolute after:inset-0 group-hover:underline group-hover:underline-offset-8" data-id="element-761" data-mp="2xikrevhsb42s">
                        {featured.title}
                      </Link>
                    </h2>
                    <p className="t-lead mt-6 max-w-[46ch] text-graphite" data-id="element-762" data-mp="3bhkgg497lkzh">{featured.excerpt}</p>
                    <p className="mt-6 text-sm text-graphite" data-id="element-763" data-mp="zui86geabdyh">{featured.readTime}</p>
                  </div>
                </article>
                {rest.length > 0 && <>
                    <TickDivider className="mt-[clamp(3rem,5vw,6rem)]" data-id="element-764" data-mp="qtsjrxrcspus" />
                    <ul className="mt-12 grid gap-x-[clamp(1rem,2vw,3rem)] gap-y-14 [grid-template-columns:repeat(auto-fill,minmax(min(100%,clamp(17rem,24vw,30rem)),1fr))]" data-id="element-765" data-mp="qu27feoxekxb">
                      {rest.map(a => <li key={a.slug} data-id="element-766" data-mp="1jkamgxo60p7o">
                          <JournalCard article={a} data-id="element-767" data-mp="1ekfroj19rfhc" />
                        </li>)}
                    </ul>
                  </>}
              </> : <p className="mt-12 text-graphite" data-id="element-768" data-mp="14sjurbax5vep">No articles in this category yet.</p>}
          </div>}

        {tab === 'videos' && <div id="panel-videos" role="tabpanel" aria-labelledby="tab-videos" data-id="element-769" data-mp="1xi8wjqvgdzhd">
            <div className="flex flex-wrap items-center justify-between gap-4" data-id="element-770" data-mp="1xg5v8aouojxc">
              <p className="max-w-[56ch] text-graphite" data-id="element-771" data-mp="3rqmfq3mhm3qw">Abby's YouTube channel is coming soon: short lessons, rituals and seasonal forecasts.</p>
              <NeedFromAbby data-id="element-772" data-mp="3i0jejg57pjhh">YouTube channel URL</NeedFromAbby>
            </div>
            <ul className="mt-10 grid gap-x-[clamp(1rem,2vw,3rem)] gap-y-12 [grid-template-columns:repeat(auto-fill,minmax(min(100%,clamp(17rem,24vw,32rem)),1fr))]" data-id="element-773" data-mp="3hygqv2rj82fx">
              {videos.map(v => <li key={v.id} data-id="element-774" data-mp="384gc096g7iu0">
                  <VideoCard video={v} data-id="element-775" data-mp="3d1q27r1nvyiw" />
                </li>)}
            </ul>
          </div>}

        {tab === 'transits' && <div id="panel-transits" role="tabpanel" aria-labelledby="tab-transits" data-id="element-776" data-mp="2z3i8rt3u3fch">
            <NeedFromAbby data-id="element-777" data-mp="3411u1cyy8sdx">current transit notes</NeedFromAbby>
            <ul className="mt-8 divide-y divide-stone border-y border-stone" data-id="element-778" data-mp="x9somynhtb72">
              {transitNotes.map(t => <li key={t.title} className="grid gap-4 py-8 md:grid-cols-[minmax(0,1fr)_minmax(0,2fr)_minmax(0,1fr)] md:gap-10" data-id="element-779" data-mp="127u8vmwnngk4">
                  <p className="t-label text-[0.64rem] text-gold-deep" data-id="element-780" data-mp="1cazkyggovpwt">{t.window}</p>
                  <div data-id="element-781" data-mp="17ftzfir78d99">
                    <h2 className="t-h3" data-id="element-782" data-mp="17c66oltkvuvl">{t.title}</h2>
                    <p className="mt-2 max-w-[60ch] text-graphite" data-id="element-783" data-mp="1l674ndcphduw">{t.summary}</p>
                  </div>
                  <p className="text-sm text-graphite md:text-right" data-id="element-784" data-mp="1gd49x1w6zyd4">{t.areas}</p>
                </li>)}
            </ul>
          </div>}
      </div>

      <NewsletterBand source="journal_letter" data-id="element-785" data-mp="1q59zy62vw5po" />
    </>;
}